import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Microscope, FlaskConical, ChevronRight, ArrowRight, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { PRODUCTS_PAGE, BRAND_PARTNERS } from "../config/content";
import Marquee from "react-fast-marquee";
import Header from "./Header";
import Footer from "./Footer";
import PageSEO from "./PageSEO";

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState(PRODUCTS_PAGE.categories[0]?.id || "lab-instruments");
  const [searchQuery, setSearchQuery] = useState("");
  
  const categoryIcons = {
    "lab-instruments": Microscope,
    "lab-consumables": FlaskConical
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return PRODUCTS_PAGE.categories;
    const q = searchQuery.toLowerCase();
    return PRODUCTS_PAGE.categories.map((category) => ({
      ...category,
      subcategories: category.subcategories
        .map((sub) => ({
          ...sub,
          products: sub.products.filter((p) => {
            const name = typeof p === "string" ? p : p.name;
            return name.toLowerCase().includes(q);
          }),
        }))
        .filter((sub) => sub.products.length > 0),
    })).filter((cat) => cat.subcategories.length > 0);
  }, [searchQuery]);

  const totalResults = useMemo(() => {
    if (!searchQuery.trim()) return 0;
    return filteredCategories.reduce(
      (sum, cat) => sum + cat.subcategories.reduce((s, sub) => s + sub.products.length, 0), 0
    );
  }, [filteredCategories, searchQuery]);

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Products"
        description="Explore our comprehensive range of lab instruments and consumables from OHAUS, HORIBA, Sartorius, PerkinElmer, Equitron, Glassco and more leading brands."
        path="/products"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="overline text-[#0047FF] mb-4">Our Products</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
              {PRODUCTS_PAGE.hero.title}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {PRODUCTS_PAGE.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Category Tabs */}
      <section className="py-6 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Search Bar */}
          <div className="relative mb-4" data-testid="product-search-container">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products... e.g. Autoclave, pH meter, Centrifuge"
              className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF] transition-colors text-base"
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="product-search-clear"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <p className="text-sm text-slate-500 mb-3" data-testid="product-search-results-count">
              {totalResults} {totalResults === 1 ? "product" : "products"} found for "<span className="font-medium text-slate-700">{searchQuery}</span>"
            </p>
          )}
          {/* Category Tabs - hidden during search */}
          {!searchQuery.trim() && (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {PRODUCTS_PAGE.categories.map((category) => {
                const Icon = categoryIcons[category.id] || Microscope;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    data-testid={`category-tab-${category.id}`}
                    className={`flex items-center gap-2 px-6 py-3 font-medium whitespace-nowrap transition-all ${
                      activeCategory === category.id
                        ? "bg-[#0047FF] text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <Icon size={20} />
                    {category.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* No results state */}
          {searchQuery.trim() && filteredCategories.length === 0 && (
            <div className="text-center py-16" data-testid="no-results">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No products found</h3>
              <p className="text-slate-500">Try a different search term or browse by category</p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 text-[#0047FF] font-medium hover:underline"
              >
                Clear search
              </button>
            </div>
          )}

          {(searchQuery.trim() ? filteredCategories : PRODUCTS_PAGE.categories).map((category) => (
            <div
              key={category.id}
              className={searchQuery.trim() || activeCategory === category.id ? "block mb-16 last:mb-0" : "hidden"}
            >
              {/* Category Header */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    {category.name}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed">
                    {category.description}
                  </p>
                </div>
                <div>
                  <img 
                    src={category.image}
                    alt={category.name}
                    className="w-full aspect-video object-cover"
                  />
                </div>
              </div>

              {/* Subcategories */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.subcategories.map((subcategory, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-slate-50 border border-slate-200 p-6 hover:border-[#0047FF] transition-colors"
                  >
                    <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4 pb-4 border-b border-slate-200">
                      {subcategory.name}
                    </h3>
                    <ul className="space-y-3">
                      {subcategory.products.map((product, pIndex) => {
                        const name = typeof product === "string" ? product : product.name;
                        const url = typeof product === "object" ? product.url : "";
                        return (
                          <li key={pIndex} className="flex items-center gap-2 text-slate-600">
                            <ChevronRight className="w-4 h-4 text-[#0047FF] flex-shrink-0" />
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-[#0047FF] hover:underline transition-colors"
                                data-testid={`product-link-${pIndex}`}
                              >
                                {name}
                              </a>
                            ) : (
                              <span>{name}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Brand Partners */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="overline text-center">Our Brand Partners</p>
        </div>
        <Marquee gradient={false} speed={40} pauseOnHover={true}>
          <div className="flex items-center gap-12 md:gap-16 px-8">
            {BRAND_PARTNERS.map((brand) => (
              <a 
                key={brand.name}
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center h-16 md:h-20 min-w-[120px] md:min-w-[160px] grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
              >
                <img src={brand.logo} alt={brand.name} className="h-full w-auto max-w-[140px] md:max-w-[180px] object-contain" loading="lazy" />
              </a>
            ))}
          </div>
        </Marquee>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0047FF]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Need a Custom Quote?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Contact us for product inquiries, bulk orders, or technical specifications.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#0047FF] hover:bg-slate-100 rounded-none px-8 py-6 text-base">
              Request Quote <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
